import { useCallback, useMemo, useState } from "react";
import { Requirement } from "../../types/data";
import { DrawerComponent } from "src/components/DialogComponent";
import { FlexCol } from "src/utils/flexLayouts";
import AddCommentGop from "../addComment/AddCommentGop";
import { PlanActionsContext } from "../../types/types";

import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { fetchCommentByContextRequest } from "../../store/comments-files/actions";
import { selectCommentByContext } from "../../selectors/selectors";
import { setLastAddedId } from "../../actions/actions";
import { fetchCommentsOnlyRequest } from "../../store/data-main/data-main";

type AddCommentProps = Pick<Requirement, "comments"> & {
  onClick?: () => void;
  context: PlanActionsContext;
};

function AddComment({ comments, onClick, context }: AddCommentProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const foundComments = useAppSelector(selectCommentByContext);

  const firstCommentId = useMemo(() => {
    if (foundComments.length > 0 && foundComments[0]?.id) {
      return foundComments[0].id;
    }
    return null;
  }, [foundComments]);

  const setOpenDrawer = (value: boolean) => {
    if (open !== value) {
      setOpen(value);
      if (value && context) {
        dispatch(setLastAddedId("gop-comments", null));
        dispatch(fetchCommentByContextRequest("gop-comments", context));
      }
    }
  };

  const closeDialog = useCallback(() => {
    setOpen(false);
    if (context?.currentDirection && context?.selectedFactory && context?.currentQuarter && context?.currentYear) {
      const params = {
        factory: context.selectedFactory.id,
        quarter: context.currentQuarter,
        year: context.currentYear,
        direction: context.currentDirection.id,
      };
      dispatch(fetchCommentsOnlyRequest(params));
    }
  }, [context, dispatch]);

  if (comments) {
    return (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="group cursor-pointer"
          onClick={() => {
            if (onClick) onClick();
            setOpenDrawer(true);
          }}
        >
          <g xmlns="http://www.w3.org/2000/svg" clip-path="url(#clip0_6012_12675)">
            <path
              d="M13.2499 2.67501C11.6749 1.07501 9.47494 0.300007 7.24994 0.550007C3.77495 0.900007 1.02494 3.67501 0.649945 7.12501C0.499945 8.72501 0.824945 10.275 1.62494 11.625L0.624945 14.225C0.474945 14.575 0.574945 15 0.874945 15.25C1.04994 15.4 1.27494 15.5 1.49994 15.5C1.64994 15.5 1.77494 15.475 1.92494 15.4L4.19994 14.25C5.57494 15.075 7.12494 15.425 8.74994 15.275C12.2499 14.95 15.0499 12.175 15.3999 8.65001C15.6249 6.42501 14.8499 4.25001 13.2499 2.67501ZM14.2749 8.55001C13.9749 11.525 11.5999 13.875 8.62494 14.15C7.14994 14.3 5.74994 13.925 4.52494 13.125C4.42494 13.05 4.32494 13.025 4.22494 13.025C4.14994 13.025 4.04994 13.05 3.97494 13.075L1.84994 14.125L2.77494 11.725C2.84994 11.55 2.82494 11.375 2.72494 11.225C1.94994 10.025 1.62494 8.62501 1.77494 7.17501C2.07494 4.30001 4.42494 1.97501 7.34994 1.67501C9.24994 1.47501 11.1249 2.12501 12.4499 3.47501C13.7999 4.80001 14.4749 6.65001 14.2749 8.55001Z"
              fill="#2D68F8"
              className="group-hover:hidden"
            />
            <path
              d="M5.92498 5.9752H9.62498C9.92497 5.9752 10.2 5.7252 10.2 5.4002C10.2 5.0752 9.94998 4.8252 9.62498 4.8252L5.92498 4.8252C5.62498 4.8252 5.34998 5.0752 5.34998 5.4002C5.34998 5.7252 5.62498 5.9752 5.92498 5.9752Z"
              fill="#2D68F8"
              className="group-hover:hidden"
            />
            <path
              d="M11.1 7.4248L5.92498 7.4248C5.62498 7.4248 5.34998 7.6748 5.34998 7.9998C5.34998 8.3248 5.59998 8.5498 5.92498 8.5498H11.1C11.4 8.5498 11.675 8.2998 11.675 7.9998C11.675 7.6998 11.4 7.4248 11.1 7.4248Z"
              fill="#2D68F8"
              className="group-hover:hidden"
            />
            <path
              d="M8.52497 10.0254H5.92498C5.62498 10.0254 5.34998 10.2754 5.34998 10.6004C5.34998 10.9254 5.59998 11.1754 5.92498 11.1754H8.49998C8.79998 11.1754 9.07498 10.9254 9.07498 10.6004C9.07498 10.2754 8.82498 10.0254 8.52497 10.0254Z"
              fill="#2D68F8"
              className="group-hover:hidden"
            />

            <path
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.2498 2.67501C11.6748 1.07501 9.47482 0.300007 7.24982 0.550007C3.77482 0.900007 1.02482 3.67501 0.649823 7.12501C0.499823 8.72501 0.824823 10.275 1.62482 11.625L0.624823 14.225C0.474823 14.575 0.574823 15 0.874823 15.25C1.04982 15.4 1.27482 15.5 1.49982 15.5C1.64982 15.5 1.77482 15.475 1.92482 15.4L4.19982 14.25C5.57482 15.075 7.12482 15.425 8.74982 15.275C12.2498 14.95 15.0498 12.175 15.3998 8.65001C15.6248 6.42501 14.8498 4.25001 13.2498 2.67501ZM9.62485 5.97503H5.92485C5.62485 5.97503 5.34985 5.72503 5.34985 5.40003C5.34985 5.07503 5.62485 4.82503 5.92485 4.82503H9.62485C9.94985 4.82503 10.1999 5.07503 10.1999 5.40003C10.1999 5.72503 9.92485 5.97503 9.62485 5.97503ZM5.92485 7.42497H11.0999C11.3999 7.42497 11.6749 7.69997 11.6749 7.99997C11.6749 8.29997 11.3999 8.54997 11.0999 8.54997L5.92485 8.54997C5.59985 8.54997 5.34985 8.32497 5.34985 7.99997C5.34985 7.67497 5.62485 7.42497 5.92485 7.42497ZM8.52485 10.0251L5.92485 10.0251C5.62485 10.0251 5.34985 10.2751 5.34985 10.6001C5.34985 10.9251 5.59985 11.1751 5.92485 11.1751L8.49985 11.1751C8.79985 11.1751 9.07485 10.9251 9.07485 10.6001C9.07485 10.2751 8.82485 10.0251 8.52485 10.0251Z"
              fill="#2D68F8"
              className="hidden group-hover:block"
            />
          </g>
          <defs xmlns="http://www.w3.org/2000/svg">
            <clipPath id="clip0_6012_12724">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <DrawerComponent open={open} zIndex={50} percentage="87" toggleDrawer={closeDialog}>
          <FlexCol className="p-10">
            <h1 className="mb-8 text-black">Комментарий</h1>
            <AddCommentGop
              toggleDoing={setOpenDrawer}
              isDisabled={true}
              firstCommentId={firstCommentId}
              isAutoSave={true}
              context={context}
              onCommentAdded={() => closeDialog()}
            />
          </FlexCol>
        </DrawerComponent>
      </>
    );
  }

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className="group cursor-pointer"
        onClick={() => setOpenDrawer(true)}
      >
        <g xmlns="http://www.w3.org/2000/svg" clip-path="url(#clip0_6012_12724)">
          <path
            d="M13.2499 2.67501C11.6749 1.07501 9.47494 0.300007 7.24994 0.550007C3.77495 0.900007 1.02494 3.67501 0.649945 7.12501C0.499945 8.72501 0.824945 10.275 1.62494 11.625L0.624945 14.225C0.474945 14.575 0.574945 15 0.874945 15.25C1.04994 15.4 1.27494 15.5 1.49994 15.5C1.64994 15.5 1.77494 15.475 1.92494 15.4L4.19994 14.25C5.57494 15.075 7.12494 15.425 8.74994 15.275C12.2499 14.95 15.0499 12.175 15.3999 8.65001C15.6249 6.42501 14.8499 4.25001 13.2499 2.67501ZM14.2749 8.55001C13.9749 11.525 11.5999 13.875 8.62494 14.15C7.14994 14.3 5.74994 13.925 4.52494 13.125C4.42494 13.05 4.32494 13.025 4.22494 13.025C4.14994 13.025 4.04994 13.05 3.97494 13.075L1.84994 14.125L2.77494 11.725C2.84994 11.55 2.82494 11.375 2.72494 11.225C1.94994 10.025 1.62494 8.62501 1.77494 7.17501C2.07494 4.30001 4.42494 1.97501 7.34994 1.67501C9.24994 1.47501 11.1249 2.12501 12.4499 3.47501C13.7999 4.80001 14.4749 6.65001 14.2749 8.55001Z"
            fill="#9CA3AF"
            className="group-hover:hidden"
          />
          <path
            d="M5.92498 5.9752H9.62498C9.92497 5.9752 10.2 5.7252 10.2 5.4002C10.2 5.0752 9.94998 4.8252 9.62498 4.8252L5.92498 4.8252C5.62498 4.8252 5.34998 5.0752 5.34998 5.4002C5.34998 5.7252 5.62498 5.9752 5.92498 5.9752Z"
            fill="#9CA3AF"
            className="group-hover:hidden"
          />
          <path
            d="M11.1 7.4248L5.92498 7.4248C5.62498 7.4248 5.34998 7.6748 5.34998 7.9998C5.34998 8.3248 5.59998 8.5498 5.92498 8.5498H11.1C11.4 8.5498 11.675 8.2998 11.675 7.9998C11.675 7.6998 11.4 7.4248 11.1 7.4248Z"
            fill="#9CA3AF"
            className="group-hover:hidden"
          />
          <path
            d="M8.52497 10.0254H5.92498C5.62498 10.0254 5.34998 10.2754 5.34998 10.6004C5.34998 10.9254 5.59998 11.1754 5.92498 11.1754H8.49998C8.79998 11.1754 9.07498 10.9254 9.07498 10.6004C9.07498 10.2754 8.82498 10.0254 8.52497 10.0254Z"
            fill="#9CA3AF"
            className="group-hover:hidden"
          />
          <path
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.2498 2.67501C11.6748 1.07501 9.47482 0.300007 7.24982 0.550007C3.77482 0.900007 1.02482 3.67501 0.649823 7.12501C0.499823 8.72501 0.824823 10.275 1.62482 11.625L0.624823 14.225C0.474823 14.575 0.574823 15 0.874823 15.25C1.04982 15.4 1.27482 15.5 1.49982 15.5C1.64982 15.5 1.77482 15.475 1.92482 15.4L4.19982 14.25C5.57482 15.075 7.12482 15.425 8.74982 15.275C12.2498 14.95 15.0498 12.175 15.3998 8.65001C15.6248 6.42501 14.8498 4.25001 13.2498 2.67501ZM9.62485 5.97503H5.92485C5.62485 5.97503 5.34985 5.72503 5.34985 5.40003C5.34985 5.07503 5.62485 4.82503 5.92485 4.82503H9.62485C9.94985 4.82503 10.1999 5.07503 10.1999 5.40003C10.1999 5.72503 9.92485 5.97503 9.62485 5.97503ZM5.92485 7.42497H11.0999C11.3999 7.42497 11.6749 7.69997 11.6749 7.99997C11.6749 8.29997 11.3999 8.54997 11.0999 8.54997L5.92485 8.54997C5.59985 8.54997 5.34985 8.32497 5.34985 7.99997C5.34985 7.67497 5.62485 7.42497 5.92485 7.42497ZM8.52485 10.0251L5.92485 10.0251C5.62485 10.0251 5.34985 10.2751 5.34985 10.6001C5.34985 10.9251 5.59985 11.1751 5.92485 11.1751L8.49985 11.1751C8.79985 11.1751 9.07485 10.9251 9.07485 10.6001C9.07485 10.2751 8.82485 10.0251 8.52485 10.0251Z"
            fill="#9CA3AF"
            className="hidden group-hover:block"
          />
        </g>
        <defs xmlns="http://www.w3.org/2000/svg">
          <clipPath id="clip0_6012_12724">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <DrawerComponent open={open} zIndex={50} percentage="87" toggleDrawer={closeDialog}>
        <FlexCol className="p-10">
          <h1 className="mb-8 text-black">Комментарий</h1>
          <AddCommentGop
            toggleDoing={setOpenDrawer}
            isDisabled={true}
            firstCommentId={firstCommentId}
            isAutoSave={true}
            context={context}
            onCommentAdded={() => closeDialog()}
          />
        </FlexCol>
      </DrawerComponent>
    </>
  );
}

export default AddComment;
